import { Button } from '@grofers/react';
import React, { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import {
  Dropdown,
  Heading,
  Textarea,
  CheckboxList,
  RadioGroup,
  Loader,
} from '../../../UiKit';
import { Star } from '../../../UiKit/Star/Star';
import {
  useGetDashboardPermissions,
  useGetDashboards,
  useGetRevokeReasons,
  useGrantAndRevoke,
} from '../apis';
import { grey } from 'sushiweb/tokens/color';
import Spinner from 'sushiweb/Spinner/Spinner';

function GrantAndRevokePermissions() {
  const [emails, setEmails] = useState('');
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [org, setOrg] = useState('');
  const [checkboxVal, setCheckboxVal] = useState([]);
  const [error, setError] = useState({});
  const dbSlugToFetchPermissions = 'lifeline_dashboard';

  const { mutate, isLoading } = useGrantAndRevoke({
    onSuccess: () => {
      setEmails('');
      setOrg('');
      setAction('');
      setCheckboxVal([]);
      setReason([]);
      setError('');
    },
  });

  const { data: dashboardData } = useGetDashboards();
  const { data: dashboardPermissions } = useGetDashboardPermissions(
    dbSlugToFetchPermissions,
  );
  const { data: reasonsData } = useGetRevokeReasons();

  const validateEmail = (email) => {
    if (!email) return false;
    if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(email)) {
      return true;
    }
    setError({ email: `${email} is an invalid email` });
    return false;
  };

  const getPermissionsList = useMemo(() => {
    if (!dashboardPermissions) return [];
    return dashboardPermissions.permissions.map(({ name, slug }) => ({
      text: name,
      type: slug,
    }));
  }, [dashboardPermissions]);

  const getDropdownValue = useMemo(() => {
    let options = {};
    if (!dashboardData) return options;
    dashboardData?.dashboards.forEach((item) => (options[item.id] = item.name));
    return options;
  }, [dashboardData]);

  const getReasonValue = useMemo(() => {
    let options = {};
    if (!reasonsData?.reasons?.length) return options;

    reasonsData?.reasons.forEach(
      (re) => (options[re.slug] = re.human_readable),
    );
    return options;
  }, [reasonsData]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!emails) return toast.error('Email is required');
    if (!action) return toast.error('Action is required');
    if (action === 'grant') {
      if (!org) return toast.error('Please select an organization');

      if (!checkboxVal.length) return toast.error('Please select permissions');
    }

    if (action === 'revoke') {
      if (!reason) return toast.error('Please select a reason');
    }

    let dataToSubmit = {};
    if (action === 'revoke') dataToSubmit.reason = getReasonValue[reason];
    let emailList = emails.split(',');
    let validEmails = true;
    emailList.forEach((email) => {
      if (!validateEmail(email)) {
        validEmails = false;
        return null;
      }
    });
    dataToSubmit.emails = emailList;
    dataToSubmit.action = action;
    dataToSubmit.permission_slugs = checkboxVal;
    dataToSubmit.org_id = org;

    mutate(dataToSubmit);
  };

  return (
    <FormConatiner>
      <div
        css={`
          display: flex;
          gap: 2rem;
          width: 100%;
          flex-wrap: wrap;
        `}
      >
        <FieldRowOne>
          <label
            css={`
              width: 100%;
            `}
          >
            <div>
              <Heading
                title='Email Ids'
                color='#1F1F1F'
                weight='600'
                size='17px'
              />
              <Star icon='*' />
            </div>
            <Textarea
              maxLimit={2000}
              showMaxLimit={true}
              showBtn={false}
              placeholder='Please enter comma separated email ids'
              className='tw-p-5 focus:tw-border-green-500 focus:tw-ring-2 focus:tw-ring-green-500 focus:tw-outline-none'
              height='12rem'
              required
              value={emails}
              onChange={(e) => {
                setEmails(e.target.value);
              }}
              onFocus={() => {
                setError((prevState) => {
                  return { ...prevState, email: '' };
                });
              }}
              style={{
                outline: `${
                  error?.email
                    ? '1px solid #BF2938'
                    : '0.5px solid rgb(207, 207, 207)'
                }`,
                borderRadius: '8px',
                minHeight: '12rem',
              }}
            />
            {error?.email && (
              <div
                css={`
                  color: #bf2938;
                  margin-top: 0.5rem;
                `}
              >
                {error?.email}
              </div>
            )}
          </label>
        </FieldRowOne>
        <hr
          color='#f8f8f8'
          css={`
            margin: 0;
          `}
        />
        <FieldRowTwo>
          <label
            onClickCapture={(event) => {
              event.preventDefault();
            }}
            css={`
              width: 20%;
            `}
          >
            <div>
              <Heading
                title='Action'
                color='#1F1F1F'
                weight='600'
                size='17px'
              />
              <Star icon='*' />
            </div>
            <div
              css={`
                display: flex;
                flex-direction: row;
                gap: 1.5rem;
                & div {
                  width: 100%;
                  display: flex;
                  align-items: center;
                }
              `}
              onClickCapture={(event) => {
                event.preventDefault();
              }}
            >
              <Dropdown
                dropDownValues={{
                  grant: 'Grant',
                  revoke: 'Revoke',
                }}
                onSelect={(value) => setAction(value)}
                dropdownVal={action}
                placeholder='Select an action'
              />
            </div>
          </label>
          <label
            css={`
              width: 40%;
              & div {
                width: 100%;
                display: flex;
                align-items: center;
              }
            `}
          >
            <div>
              <Heading
                title='Organization'
                color='#1F1F1F'
                weight='600'
                size='17px'
              />
              {action === 'grant' && <Star icon='*' />}
            </div>
            <Dropdown
              dropDownValues={getDropdownValue}
              onSelect={(value) => setOrg(value)}
              dropdownVal={org}
              placeholder='Please select an Organization'
            />
          </label>
          <div
            style={{
              cursor: `${action === 'revoke' ? 'pointer' : 'not-allowed'}`,
              width: '40%',
            }}
          >
            {' '}
            <label
              style={{
                pointerEvents: `${action === 'revoke' ? 'auto' : 'none'}`,
              }}
              css={`
                width: 100%;
                & div {
                  width: 100%;
                  display: flex;
                  align-items: center;
                }
              `}
            >
              <div>
                <Heading
                  title='Reason'
                  color={`${action === 'revoke' ? '#1F1F1F' : '#B8B8B8'}`}
                  weight='600'
                  size='17px'
                />
                {action === 'revoke' && <Star icon='*' />}
              </div>
              <Dropdown
                dropDownValues={getReasonValue}
                onSelect={(value) => setReason(value)}
                dropdownVal={reason}
                placeholder='Please select a reason'
              />
            </label>
          </div>
        </FieldRowTwo>
        <FieldRowThree>
          {org && getPermissionsList.length > 0 ? (
            <label>
              <div>
                <Heading
                  title='Permission List'
                  color='#1F1F1F'
                  weight='600'
                  size='17px'
                />
                {action === 'grant' && <Star icon='*' />}
              </div>

              <CheckboxList
                items={getPermissionsList}
                selectOptions={(val) => setCheckboxVal(val)}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '1rem',
                }}
                listItemWidth='25rem'
              />
            </label>
          ) : null}
        </FieldRowThree>
      </div>
      <div
        css={`
          width: 8rem;
        `}
      >
        <Button
          onClick={handleSubmit}
          size='medium'
          variant='fill'
          style={{ padding: '1rem 1.7rem' }}
          className='tw-h-16 tw-w-full'
        >
          {isLoading ? (
            <Spinner size='small' color={`${grey.z300}`} />
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </FormConatiner>
  );
}

export default GrantAndRevokePermissions;

const FormConatiner = styled.div`
  width: 100%;
  height: fit-content;
  background-color: #fff;
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  gap: 4rem;
  flex-direction: column;
`;

const FieldRowOne = styled.div`
  width: 100%;
  display: flex;
  gap: 3rem;
`;

const FieldRowTwo = styled.div`
  width: 100%;
  display: flex;
  gap: 3rem;
`;

const FieldRowThree = styled.div`
  width: 100%;
  display: flex;
`;
